import { supabase } from '../lib/supabase';

// Buscar todos os usuários escolares
export const getAllSchoolUsers = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        escolas:school_id(id, nome),
        funcoes_usuarios:funcao_id(id, nome, cor, icone)
      `)
      .eq('role', 'admin')
      .eq('school_adm', true)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Erro ao buscar usuários escolares:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Buscar usuário por ID
export const getSchoolUserById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        escolas:school_id(id, nome),
        funcoes_usuarios:funcao_id(id, nome, cor, icone)
      `)
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', true)
      .single();

    if (error) throw error;

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Erro ao buscar usuário:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Criar novo usuário escolar
export const createSchoolUser = async (userData) => {
  try {
    // Salvar sessão atual
    const { data: currentSession } = await supabase.auth.getSession();
    
    // Criar usuário no Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: { 
          name: userData.name, 
          role: 'admin',
          school_adm: true,
          school_id: userData.school_id,
          funcao_id: userData.funcao_id
        }
      }
    });

    if (authError) throw new Error(authError.message);

    if (!authData.user) {
      throw new Error('Erro ao criar usuário');
    }

    // Criar perfil adicional (caso o trigger não funcione)
    const { error: profileError } = await supabase
      .from('profiles')
      .upsert({
        id: authData.user.id,
        email: userData.email,
        name: userData.name,
        phone: userData.phone || '',
        department: userData.department || '',
        role: 'admin',
        school_adm: true,
        school_id: userData.school_id,
        funcao_id: userData.funcao_id || null,
        is_active: userData.is_active !== undefined ? userData.is_active : true
      });

    if (profileError) {
      console.error('Erro ao criar perfil:', profileError);
      // Não falha aqui pois o trigger pode ter criado o perfil
    }

    // Restaurar sessão anterior
    if (currentSession?.session) {
      await supabase.auth.setSession(currentSession.session);
    } else {
      await supabase.auth.signOut();
    }

    return {
      success: true,
      data: authData.user
    };
  } catch (error) {
    console.error('Erro ao criar usuário escolar:', error);
    
    // Restaurar sessão em caso de erro
    try {
      const { data: currentSession } = await supabase.auth.getSession();
      if (currentSession?.session) {
        await supabase.auth.setSession(currentSession.session);
      }
    } catch (sessionError) {
      console.error('Erro ao restaurar sessão:', sessionError);
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

// Atualizar usuário escolar
export const updateSchoolUser = async (id, userData) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        name: userData.name,
        phone: userData.phone,
        department: userData.department,
        school_id: userData.school_id,
        funcao_id: userData.funcao_id || null,
        is_active: userData.is_active,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', true)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Erro ao atualizar usuário:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Excluir usuário escolar (apenas perfil)
export const deleteSchoolUser = async (id) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', true);

    if (error) throw error;

    return {
      success: true
    };
  } catch (error) {
    console.error('Erro ao excluir usuário:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Alternar status ativo/inativo
export const toggleSchoolUserStatus = async (id, isActive) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', true)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Erro ao alterar status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Buscar usuários com filtro
export const searchSchoolUsers = async (searchTerm, filters = {}) => {
  try {
    let query = supabase
      .from('profiles')
      .select(`
        *,
        escolas!school_id (
          id,
          nome
        ),
        funcoes_usuarios:funcao_id (
          id,
          nome,
          cor,
          icone
        )
      `)
      .eq('role', 'admin')
      .eq('school_adm', true);

    // Aplicar filtro de busca
    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,department.ilike.%${searchTerm}%`);
    }

    // Aplicar filtros adicionais
    if (filters.school_id) {
      query = query.eq('school_id', filters.school_id);
    }

    if (filters.is_active !== undefined) {
      query = query.eq('is_active', filters.is_active);
    }

    if (filters.department) {
      query = query.eq('department', filters.department);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Obter estatísticas dos usuários escolares
export const getSchoolUsersStats = async (schoolId = null) => {
  try {
    let query = supabase
      .from('profiles')
      .select('is_active')
      .eq('role', 'admin')
      .eq('school_adm', true);

    // Se schoolId for fornecido, filtrar por escola
    if (schoolId) {
      query = query.eq('school_id', schoolId);
    }

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      total: data.length,
      active: data.filter(user => user.is_active).length,
      inactive: data.filter(user => !user.is_active).length
    };

    return {
      success: true,
      data: stats
    };
  } catch (error) {
    console.error('Erro ao obter estatísticas:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Buscar todas as escolas disponíveis
export const getAllSchools = async () => {
  try {
    const { data, error } = await supabase
      .from('escolas')
      .select('id, nome')
      .order('nome');

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Erro ao buscar escolas:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Buscar todos os usuários com informações relacionadas
export const getAllUsers = async () => {
  try {
    console.log('🔍 Iniciando busca de usuários...');
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('name');

    console.log('📊 Resultado da query profiles:', { data, error });
    console.log('📈 Total de usuários encontrados:', data?.length || 0);

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('❌ Erro ao buscar usuários:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Obter perfil do usuário logado
export const getCurrentUserProfile = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    console.log('👤 Usuário autenticado:', user.id);

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    console.log('🔑 Perfil do usuário logado:', data);
    console.log('🏫 school_adm:', data?.school_adm);
    console.log('🏢 school_id:', data?.school_id);

    if (error) throw error;

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('❌ Erro ao buscar perfil do usuário:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Buscar todas as funções de usuários
export const getAllUserFunctions = async () => {
  try {
    const { data, error } = await supabase
      .from('funcoes_usuarios')
      .select('*')
      .eq('is_active', true)
      .order('nome');

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Erro ao buscar funções de usuários:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
